import { useState, useEffect } from 'react';
import { useProjectContext, Card, Form, Animate, BrandRampPreview } from 'components/lib';
import { useTranslate } from 'app/translations';
import { validateDomain, validateEmail } from 'utilities/form';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';

export function ProjectForm(props) {
    const { _t } = useTranslate();
    const paramsId = useParams();
    const projectId = JSON.parse(localStorage.getItem('project_id')) || props.id || paramsId.id;
    const { projects } = useProjectContext();
    const queryClient = useQueryClient();
    const project = props.providedProject || projects?.find((p) => p.id === projectId);
    const [formData, setFormData] = useState({});

    const domainField = {
        name: 'domain',
        slug: 'project_domain',
        type: 'textList',
        label: _t('project.privacy.domain.label'),
        url: `/api/project/private/field/${props.id}`,
        id: props.id,
        method: 'POST',
        required: false,
        placeholder: `Ex: ${_t('email.label').toLowerCase()}.com`,
        onBlur: validateDomain,
        ignoreOnSubmit: true,
    };

    const emailField = {
        name: 'email',
        slug: 'project_email',
        label: _t('project.privacy.email.label'),
        type: 'textList',
        url: `/api/project/private/field/${props.id}`,
        id: props.id,
        method: 'POST',
        required: false,
        placeholder: `Ex: ${_t('exemple')}@mail.com`,
        onBlur: validateEmail,
        ignoreOnSubmit: true,
    };

    const createFormData = () => {
        if (!project) return {};
        return {
            logo: {
                label: 'Logo',
                type: 'logo',
                required: false,
                placeholder: project?.file_path || '',
                // LogoFileInput has always accepted these; no call site passed
                // them, so a logo was unbounded and any mime went through. It
                // now renders on the public intake page, so bound it.
                // 'jpeg', NOT 'jpg' — the input compares the mime SUBTYPE, so
                // 'jpg' silently rejects every JPEG.
                accept: ['png', 'jpeg', 'webp'],
                maxFileSize: 2,
                max: 1,
            },
            // White label. Empty means "use the Valetify palette", which is why
            // the field is not required and '' is a valid value.
            brand_color: {
                label: _t('publicIntake.brand.colorLabel'),
                type: 'color',
                required: false,
                value: project?.brand?.color || '',
                hint: _t('publicIntake.brand.colorHint'),
                // 600 is the hex as-is; 800-900 may still darken for the
                // sign-in panel. The preview is how the admin sees that.
                preview: (color) => (
                    <BrandRampPreview
                        color={color}
                        label={_t('publicIntake.brand.rampPreview')}
                    />
                ),
            },
            name: {
                label: _t('project.name'),
                type: 'text',
                required: true,
                value: project?.name || '',
            },
            privacy: {
                label: _t('project.privacy.label'),
                type: 'select',
                required: true,
                options: [
                    { value: 'private', label: _t('private') },
                    { value: 'public', label: _t('public') },
                ],
                default: project?.privacy || 'private',
                onChange: (value) => {
                    setFormData((prev) => {
                        if (value === 'private') {
                            prev.domain = domainField;
                            prev.email = emailField;
                        } else {
                            delete prev.domain;
                            delete prev.email;
                        }
                        return { ...prev };
                    });
                },
            },
        };
    };

    useEffect(() => {
        const projectForm = createFormData();
        if (project?.privacy === 'private') {
            projectForm.domain = domainField;
            projectForm.email = emailField;
        }
        setFormData(projectForm);
    }, [projectId, project]);
  
    if (!Object.keys(formData).length) return null;

    return (
        <Animate>
            <Card
                title={props.sectionTitle || (props.disableTitle ? null : project?.name)}
                description={props.sectionDescription}
                className="w-full">
                <Form
                    data={formData}
                    updateOnChange
                    callback={(res) => {
                        if (res.status === 200) {
                            queryClient.invalidateQueries('projects');
                            props.callback && props.callback(res);
                        }
                    }}
                    buttonText={_t('save')}
                    url={props.url}
                    method={props.method}
                />
                {props.children}
            </Card>
        </Animate>
    );
}
